import React, { useCallback, useMemo } from 'react';
import {
  withGoogleMap,
  GoogleMap,
  Marker,
  WithGoogleMapProps,
} from 'react-google-maps';

import locationMarker from '../../assets/images/pin.png';
import markerBase from '../../assets/images/marker-easy.png';
import markerBaseSelected from '../../assets/images/marker-base-selected.png';
import markerHome from '../../assets/images/home-marker.png';
import markerTruck from '../../assets/images/truck-marker.png';
import attemptsPoint from '../../assets/images/attempts-point.png';

export type LatLng = {
  lat: number;
  lng: number;
};

export enum MapType {
  Satellite = 'satellite',
  Roadmap = 'roadmap',
}

type ValueOf<T> = T[keyof T];

export const markerIcons = {
  location: {
    anchor: { y: 47, x: 24 },
    iconSrc: locationMarker,
  },
  base: {
    anchor: { y: 20, x: 5 },
    iconSrc: markerBase,
  },
  baseSelected: {
    anchor: { y: 20, x: 5 },
    iconSrc: markerBaseSelected,
  },
  home: {
    anchor: { y: 47, x: 24 },
    iconSrc: markerHome,
  },
  truck: {
    anchor: { y: 47, x: 24 },
    iconSrc: markerTruck,
  },
  deliveryPoint: {
    anchor: { y: 0, x: 0 },
    iconSrc: attemptsPoint,
  },
};

export type MarkerType = {
  lat: number;
  lng: number;
  icon: ValueOf<typeof markerIcons>;
};

type MapProps = {
  zoom?: number;
  mapHeight?: string;
  markers?: MarkerType[];
  centerMap: { lat: number; lng: number };
  isGestureEnabled?: boolean;
  hasZoomControl?: boolean;
  mapType?: MapType;
  hasMapTypeControl?: boolean;
  showPoi?: boolean;
  onMarkerClick?: (e?: LatLng) => void;
  onClick?: (latLng: LatLng) => void;
  onGoogleApiLoaded?: any;
  currentZoom?: number;
  currentCenterMap?: { lat: number; lng: number };
};

const Map = withGoogleMap(
  ({
    onClick,
    zoom,
    currentZoom,
    markers = [],
    centerMap,
    currentCenterMap,
    onMarkerClick,
    showPoi = true,
    isGestureEnabled = true,
    hasZoomControl = false,
    hasMapTypeControl = false,
    mapType = MapType.Roadmap,
  }: WithGoogleMapProps & MapProps) => {
    const onMapClick = useCallback(
      (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        onClick?.({ lat, lng });
      },
      [onClick]
    );

    const emitMarkerClick = useCallback(
      (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        onMarkerClick?.({ lat, lng });
      },
      [onMarkerClick]
    );

    const styles = useMemo(() => {
      if (showPoi) return undefined;

      return [
        {
          featureType: 'poi',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
      ];
    }, [showPoi]);

    return (
      <GoogleMap
        defaultZoom={zoom}
        zoom={currentZoom || zoom}
        defaultCenter={centerMap}
        center={currentCenterMap || centerMap}
        defaultOptions={{
          tilt: 0,
          panControl: false,
          zoomControl: hasZoomControl,
          mapTypeControl: hasMapTypeControl,
          mapTypeId: mapType,
          scaleControl: false,
          streetViewControl: false,
          overviewMapControl: false,
          rotateControl: false,
          gestureHandling: isGestureEnabled ? undefined : 'none',
          styles: styles,
        }}
        onClick={onMapClick}
      >
        {markers.map((m, i) => {
          return (
            <Marker
              key={i}
              icon={{ url: m.icon.iconSrc, anchor: m.icon.anchor }}
              position={{ lat: m.lat, lng: m.lng }}
              onClick={emitMarkerClick}
            />
          );
        })}
      </GoogleMap>
    );
  }
);

export const ComposedMaps: React.FC<MapProps> = (props) => {
  const mapHeight = props.mapHeight || '300px';

  return (
    <Map
      containerElement={<div style={{ height: mapHeight }} />}
      mapElement={<div style={{ height: `100%` }} />}
      {...props}
    />
  );
};
